/*
 * flashmeeting-fe
 *
 * Author: Andgel BRISSAUD
 * Copyright (c) 2026 Prodeo - All Rights Reserved
 */

/* ===== External Libraries ===== */
import { AXIOS, CallbackErrorAxios, CallbackWarningAxios, CommonGlobalService, UUID } from '@umanforge/common';
import { AxiosError, AxiosResponse } from 'axios';
import { Dispatch } from '@reduxjs/toolkit';

/* ===== API Configuration ===== */
import { API } from 'constants/api.constant';

/* ===== Types and Interfaces ===== */
import { ChartUnitApi } from 'interfaces/chartUnit.interface';

/* ===== Services ===== */
import { GlobalService } from './global.service';

export class ChartUnitService {

  /* ===== Get chart units by event manager ===== */
  static getByEventManager(
    eventManagerId: UUID,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<ChartUnitApi[]> {
    return new Promise( async resolve => {
      AXIOS.get( `${API.CHART_UNITE}/${eventManagerId}`, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse<ChartUnitApi[]> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  /* ===== Create chart unit ===== */
  static createChartUnit(
    body: { eventManagerId: UUID; value: string },
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<UUID | undefined> {
    return new Promise( async resolve => {
      AXIOS.post( API.CHART_UNITE, body, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse<{ id: UUID }> ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.data?.id );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  /* ===== Update chart unit ===== */
  static updateChartUnit(
    chartUnitId: UUID,
    body: { value: string },
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.put( `${API.CHART_UNITE}/${chartUnitId}`, body, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 200 || response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }

  /* ===== Delete chart unit ===== */
  static deleteChartUnit(
    chartUnitId: UUID,
    token: string | null, dispatch: Dispatch,
    onError?: CallbackErrorAxios, onWarning?: CallbackWarningAxios
  ): Promise<boolean> {
    return new Promise( async resolve => {
      AXIOS.delete( `${API.CHART_UNITE}/${chartUnitId}`, await CommonGlobalService.getHeaderRequest( token ) )
        .then( ( response: AxiosResponse ) => {
          CommonGlobalService.saveRequestToken( response, dispatch );
          if ( response.status === 202 ) {
            GlobalService.onAxiosWarning( response, onWarning );
          }
          resolve( response.status === 200 || response.status === 201 );
        } )
        .catch( ( err: AxiosError ) => {
          GlobalService.onAxiosError( err, dispatch, onError );
        } );
    } );
  }
}
